import { FormControl, Grid, Stack, TextField, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { FormField, useFormStore } from '@tom-ui/utils'
import { EquipmentTypes, YardBlockDto } from '@yard/app/api'
import { FormProps } from '@yard/app/models/form-props.model'
import { EquipmentTypeAutoComplete } from '@yard/components/AutoCompletes/EquipmentTypeAutoComplete/EquipmentTypeAutoComplete'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { IFlexibleBlockFormData } from '../../models/yard-setup.model'

interface Props extends FormProps {
  flexibleBlock?: YardBlockDto
  onSubmit: (formValues: IFlexibleBlockFormData) => Promise<void> | void
}

const parseAcceptedLengths = (value: string): { acceptedLengths: number[]; isValid: boolean } => {
  const tokens = value
    .split(',')
    .map(length => length.trim())
    .filter(Boolean)

  const acceptedLengths = tokens.map(length => Number(length))
  const isValid = acceptedLengths.every(length => Number.isInteger(length) && length > 0)

  return {
    acceptedLengths: [...new Set(acceptedLengths)],
    isValid,
  }
}

const formatAcceptedLengths = (acceptedLengths?: number[]): string => acceptedLengths?.join(', ') ?? ''

interface AcceptedLengthsFieldProps {
  value?: number[]
  onChange: (value: number[]) => void
  onValidityChange: (isValid: boolean) => void
  label: string
  helperText: string
  invalidText: string
}

const AcceptedLengthsField = ({
  value,
  onChange,
  onValidityChange,
  label,
  helperText,
  invalidText,
}: AcceptedLengthsFieldProps) => {
  const [inputValue, setInputValue] = useState(formatAcceptedLengths(value))
  const [error, setError] = useState<string>()

  const setParsedValue = (rawValue: string) => {
    const parsed = parseAcceptedLengths(rawValue)
    onValidityChange(parsed.isValid)

    if (!parsed.isValid) {
      setError(invalidText)
      return parsed
    }

    setError(undefined)
    onChange(parsed.acceptedLengths)
    return parsed
  }

  const commitValue = () => {
    const parsed = setParsedValue(inputValue)
    if (!parsed.isValid) {
      return
    }

    setInputValue(formatAcceptedLengths(parsed.acceptedLengths))
  }

  return (
    <TextField
      fullWidth
      label={label}
      name='acceptedLengths'
      helperText={error ?? helperText}
      error={!!error}
      value={inputValue}
      onBlur={commitValue}
      onChange={event => {
        const nextValue = event.target.value
        setInputValue(nextValue)
        setParsedValue(nextValue)
      }}
    />
  )
}

export const FlexibleBlockForm = observer(({ id, flexibleBlock, onSubmit }: Props) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  const formStore = useFormStore<IFlexibleBlockFormData>()
  const [acceptedLengthsValid, setAcceptedLengthsValid] = useState(true)

  const handleSubmit = () => {
    if (formStore.validate() && acceptedLengthsValid) {
      onSubmit(formStore.data)
    }
  }

  return (
    <Stack
      id={id}
      component='form'
      autoComplete='off'
      noValidate
      onSubmit={event => {
        event.preventDefault()
        handleSubmit()
      }}
      spacing={theme.customSpacing.m}
    >
      <FormField
        formStore={formStore}
        name={'name'}
        initialValue={flexibleBlock?.name ?? ''}
        validate={value => {
          if (!value) return t('fieldIsRequired', 'Field is required')
        }}
      >
        {store => (
          <Stack>
            <TextField
              {...store.props}
              fullWidth
              label={`${t('name', 'Name')}`}
              required
              name='name'
            />
            {store.error && (
              <Typography variant='caption' color='error'>
                {store.error}
              </Typography>
            )}
          </Stack>
        )}
      </FormField>

      {!flexibleBlock && (
        <Grid size={8}>
          <FormField<IFlexibleBlockFormData, EquipmentTypes[] | undefined>
            formStore={formStore}
            name={'equipmentTypes'}
            initialValue={undefined}
            validate={value => {
              if (!value) return t('fieldIsRequired', 'Field is required')
            }}
          >
            {store => (
              <FormControl fullWidth>
                <EquipmentTypeAutoComplete multiple onChange={store.setValue} />
              </FormControl>
            )}
          </FormField>
        </Grid>
      )}

      <FormField<IFlexibleBlockFormData, number[]>
        formStore={formStore}
        name={'acceptedLengths'}
        initialValue={flexibleBlock?.acceptedLengths ?? []}
      >
        {store => (
          <AcceptedLengthsField
            label={t('acceptedLengths', 'Accepted lengths')}
            helperText={t('acceptedLengthsHelp', 'Comma-separated container lengths. Leave empty for Any.')}
            invalidText={t('acceptedLengthsInvalid', 'Use comma-separated positive whole numbers.')}
            value={store.value}
            onChange={store.setValue}
            onValidityChange={setAcceptedLengthsValid}
          />
        )}
      </FormField>
    </Stack>
  )
})
